///<reference types="chrome"/>

import { createContext } from "react";
import { FetchOptions, Font, PluginContextType } from "./types";
import { OutputWindow, WindowSize } from '../settings/types';
import { Resource } from "../resources/types";
import { Theme } from "../themes/types";

const findPluginIDFromMeta = () => document.head.querySelector("meta[name='X-JustWords-Extension']")?.getAttribute('value');

class NotInitialisedError extends Error {}

/**
 * A class to encapsulate all intercommunication between the web app and the
 * Chrome plugin.
 */
export class PluginContext implements PluginContextType {

  _plugin_id: string | null = null;

  public initialised: boolean = false;

  setPluginID(id: string) {
    this._plugin_id = id;
  };

  _tryConnect = (resolve: () => void, reject: () => void, attempts: number) => {
    if (this._plugin_id && this.initialised) {
      resolve();
    }
    const pluginID = findPluginIDFromMeta();
    if (pluginID) {
      this._plugin_id = pluginID;
      this.initialised = true;
      resolve();
    }
    else if (attempts > 0) {
      setTimeout(
        this._tryConnect,
        100,
        resolve, reject, attempts - 1
      );
    }
    else {
      reject();
    }
  };

  findAndConnect() {
    return new Promise<void>(
      (resolve, reject) => {
        this._tryConnect(resolve, reject, 5);
      }
    );
  }

  async loadResources(accountID: string) {
    const result = await this.send({
      message: 'storage_get',
      key: `justwords_resources/${accountID}`
    });
    return result;
  }

  async saveResources(accountID: string, resources: Record<string, Resource>) {
    const result = await this.send({
      message: 'storage_set',
      key: `justwords_resources/${accountID}`,
      value: resources
    });
    return result;
  }

  async loadThemes(accountID: string) {
    const result = await this.send({
      message: 'storage_get',
      key: `justwords_themes/${accountID}`
    });
    return result;
  }

  async saveThemes(accountID: string, themes: Record<string, Theme>) {
    const result = await this.send({
      message: 'storage_set',
      key: `justwords_themes/${accountID}`,
      value: themes
    });
    return result;
  }

  async getInstalledFonts() {
    return await this.send({ message: 'get_installed_fonts' }) as Font[];
  }

  openOutputWindow(window: OutputWindow, canvasSize: WindowSize, index: number) {
    this.sendNoWait({
      message: 'open_output_window',
      index,
      display: canvasSize,
      ...window
    });
  }

  fetch(url: string, options: FetchOptions = {}) {
    return this.send({
      message: 'cors_fetch',
      url,
      ...options
    });
  }

  send(message: object) {
    if (!this.initialised) {
      throw new NotInitialisedError();
    }
    return new Promise(
      (resolve) => {
        window.chrome.runtime.sendMessage(
          this._plugin_id,
          message,
          resolve
        );
      }
    );
  }

  sendNoWait(message: object) {
    if (!this.initialised) {
      throw new NotInitialisedError();
    }
    window.chrome.runtime.sendMessage(
      this._plugin_id,
      message
    );
  }
}

export const context = createContext<PluginContextType>(new PluginContext());
